/**
 * Videos Vertical Block - Carousel & Interaction Logic
 * Features: Drag-to-scroll, navigation buttons, video playback, modal
 */

// ==========================================================================
// Drag-to-scroll functionality (IIFE pattern)
// ==========================================================================
(function initDragScroll() {
  let isDragging = false;
  let startX = 0;
  let scrollLeft = 0;
  let currentCarousel = null;
  let hasDragged = false;

  function handleMouseDown(e) {
    if (e.target.closest('[data-play-button]')) return;

    const carousel = e.target.closest('[data-video-carousel]');
    if (!carousel) return;

    isDragging = true;
    hasDragged = false;
    currentCarousel = carousel;
    startX = e.pageX - carousel.offsetLeft;
    scrollLeft = carousel.scrollLeft;
    carousel.classList.add('is-dragging');
    e.preventDefault();
  }

  function handleMouseMove(e) {
    if (!isDragging || !currentCarousel) return;
    e.preventDefault();

    const x = e.pageX - currentCarousel.offsetLeft;
    const distance = x - startX;

    if (Math.abs(distance) > 5) {
      hasDragged = true;
    }

    currentCarousel.scrollLeft = scrollLeft - distance;
  }

  function handleMouseUp() {
    if (!currentCarousel) return;

    isDragging = false;
    currentCarousel.classList.remove('is-dragging');

    // Prevent click events after drag
    if (hasDragged) {
      setTimeout(() => {
        hasDragged = false;
      }, 100);
    }

    currentCarousel = null;
  }

  function handleClick(e) {
    if (hasDragged) {
      const isPlayButton = e.target.closest('[data-play-button]');
      if (!isPlayButton) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    }
  }

  document.addEventListener('mousedown', handleMouseDown);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
  document.addEventListener('click', handleClick, true);
})();

// ==========================================================================
// VideosVertical Class
// ==========================================================================
class VideosVertical {
  constructor(container) {
    this.container = container;
    this.carousel = container.querySelector('[data-video-carousel]');
    this.prevBtn = container.querySelector('[data-video-prev]');
    this.nextBtn = container.querySelector('[data-video-next]');
    this.cards = Array.from(container.querySelectorAll('[data-video-card]'));

    if (!this.carousel) return;

    this.init();
  }

  init() {
    this.updateCardSizes();

    window.addEventListener('resize', () => this.updateCardSizes());

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate('prev'));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate('next'));
    }

    this.carousel.addEventListener('scroll', () => this.updateNavigationButtons());

    this.initPlayButtons();

    this.updateNavigationButtons();

    const observer = new MutationObserver(() => {
      this.cards = Array.from(this.container.querySelectorAll('[data-video-card]'));
      this.updateCardSizes();
      this.initPlayButtons();
    });

    observer.observe(this.carousel, { childList: true });
  }

  updateCardSizes() {
    const containerStyles = getComputedStyle(this.container);
    const cardsPerView = parseFloat(containerStyles.getPropertyValue('--cards-per-view')) || 4;
    const cardGap = parseFloat(containerStyles.getPropertyValue('--card-gap')) || 16;
    // When the carousel overflows to the right, the wrapper is widened past
    // the section's content area. We must exclude that extension from the
    // card width calculation so the extra space reveals partial cards
    // (drag-to-scroll affordance) instead of stretching every card.
    const overflowExtension = parseFloat(containerStyles.getPropertyValue('--overflow-extension')) || 0;
    const effectiveWidth = this.carousel.offsetWidth - overflowExtension;

    const totalGapSpace = cardGap * (cardsPerView - 1);
    const cardWidth = (effectiveWidth - totalGapSpace) / cardsPerView;

    this.carousel.style.setProperty('--card-width', `${cardWidth}px`);
  }

  navigate(direction) {
    const cardWidth = this.cards[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.container).getPropertyValue('--card-gap')) || 16;
    const scrollAmount = cardWidth + gap;

    if (direction === 'prev') {
      this.carousel.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      this.carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }

  updateNavigationButtons() {
    if (!this.prevBtn || !this.nextBtn) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.carousel;
    const isAtStart = scrollLeft <= 10;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

    this.prevBtn.classList.toggle('is-hidden', isAtStart);
    this.nextBtn.classList.toggle('is-hidden', isAtEnd);
  }

  initPlayButtons() {
    this.cards.forEach(card => {
      if (card.dataset.initialized) return;
      card.dataset.initialized = 'true';

      const playButton = card.querySelector('[data-play-button]');
      const video = card.querySelector('[data-video]');
      const wrapper = card.querySelector('[data-video-wrapper]');

      if (!video || !wrapper) return;

      const showControls = video.dataset.showControls === 'true';
      const isIframe = video.tagName === 'IFRAME';

      const playVideo = (e) => {
        e.preventDefault();
        e.stopPropagation();
        card.classList.add('is-playing');
        
        if (isIframe) {
          video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else {
          if (showControls) {
            video.controls = true;
          }
          video.play().catch(err => console.error('Play prevented:', err));
        }
      };

      if (playButton) {
        playButton.addEventListener('click', playVideo);
      }

      if (!isIframe) {
        video.addEventListener('pause', () => {
          if (video.currentTime === 0) {
            card.classList.remove('is-playing');
            if (showControls) {
              video.controls = false;
            }
          }
        });

        video.addEventListener('ended', () => {
          card.classList.remove('is-playing');
          video.currentTime = 0;
          if (showControls) {
            video.controls = false;
          }
        });
      }
    });
  }
}

// ==========================================================================
// Initialization
// ==========================================================================
function initializeVideosVertical() {
  const containers = document.querySelectorAll('[data-videos-vertical]');
  containers.forEach(container => new VideosVertical(container));
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVideosVertical);
} else {
  initializeVideosVertical();
}

// Theme editor support
document.addEventListener('shopify:section:load', (e) => {
  const container = e.target.querySelector('[data-videos-vertical]');
  if (container) {
    new VideosVertical(container);
  }
});

document.addEventListener('shopify:block:select', (e) => {
  const container = e.target.closest('[data-videos-vertical]');
  if (container) {
    const videoCard = e.target.closest('[data-video-card]');
    if (videoCard) {
      videoCard.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }
});

document.addEventListener('shopify:block:load', (e) => {
  const container = e.target.closest('[data-videos-vertical]');
  if (container) {
    setTimeout(() => {
      const instance = new VideosVertical(container);
      instance.updateCardSizes();
    }, 100);
  }
});
